import React from 'react';
import {
    SimpleShowLayout,
    FunctionField,
    Edit,
    SimpleForm,
    NumberInput,
    DateInput,
    SelectInput,
    required,
    minValue,
    useTranslate,
    maxLength,
    useRecordContext,
} from 'react-admin';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import DeviceCollectionPointField from '@/components/device/DeviceCollectionPointField.jsx';
import { DeviceStateField } from '@/components/device/fields/DeviceStateField.jsx';
import SelectWithIcon from '@/components/global/inputs/SelectWithIcon';
import { serviceRequestPriorityChoices, serviceRequestStatusChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { ServiceRequestSourceField } from '@/pages/serviceRequest.jsx';
import { CoordinatorInput, ServicemanInput, UsedPartsInput } from '@/components/service/forms/ServiceForm.jsx';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import EditableField from '@/components/global/fields/EditableField';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { formatTimePassedExpanded } from '@/helpers/date/formatTimePassed';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import ServiceDate from '@/components/service/ServiceDate';

export const SERVICE_SUMMARY_WORKING_TIME_ANCHOR_ID = 'service-summary-working-time';
export const SERVICE_SUMMARY_PLANNED_DATE_ANCHOR_ID = 'service-summary-planned-date';

const noop = () => {};

const buildCanEditField = (canEditAllFields, canEditLimitedFields) => {
    return fieldName => {
        if (canEditAllFields) return true;
        if (canEditLimitedFields) {
            return fieldName === 'status' || fieldName === 'workingTime' || fieldName === 'difficulty' || fieldName === 'usedParts';
        }
        return false;
    };
};

const buildServiceSummaryPayload = ({ formData, record, isEditing, fields }) => {
    const serviceNoteValue = formData['collectionPoint.serviceNote'] || formData?.collectionPoint?.serviceNote;
    const isEditingServiceNote = isEditing(fields.SERVICE_NOTE);
    const isEditingPlannedDate = isEditing(fields.PLANNED_DATE);
    const isEditingDifficulty = isEditing(fields.DIFFICULTY);
    const hasDifficulty = Object.hasOwn(formData, 'difficulty');
    const difficultyValue = hasDifficulty ? formData.difficulty : record?.difficulty;

    return {
        description: formData.description ?? record?.description ?? null,
        coordinatorId: formData.coordinatorId ?? record?.coordinatorId ?? null,
        servicemanId: formData.servicemanId ?? record?.servicemanId ?? null,
        usedPartIds: formData.usedPartIds ?? null,
        status: formData.status ?? record?.status,
        priority: formData.priority ?? record?.priority,
        type: formData.type ?? record?.type,
        workingTime: formData.workingTime ?? record?.workingTime ?? 0,
        serviceNote: isEditingServiceNote && serviceNoteValue !== undefined ? serviceNoteValue || null : record?.collectionPoint?.serviceNote || null,
        scheduledDate: isEditingPlannedDate ? formData.scheduledDate ?? record?.scheduledDate ?? null : record?.scheduledDate ?? null,
        difficulty: isEditingDifficulty ? difficultyValue ?? null : record?.difficulty ?? null,
    };
};

const StatusField = ({ translate, isEditing, fields, onEdit, canEditField, defaultStatus, canEditLimitedFields }) => {
    const statusChoices = serviceRequestStatusChoices(translate);
    const editableChoices = canEditLimitedFields ? statusChoices.filter(choice => choice.id === 'inProgress' || choice.id === 'fixed') : statusChoices;

    return (
        <EditableField
            isEditing={isEditing(fields.STATUS)}
            onEdit={canEditField('status') ? onEdit : noop}
            displayValue={record => {
                const status = statusChoices.find(choice => choice.id === record.status);
                return status ? status.name : record.status;
            }}
            isFilled={record => record.status}
            colorChoice={record => {
                return statusChoices.find(choice => choice.id === record.status);
            }}
            editComponent={
                <SelectWithIcon
                    source='status'
                    label={translate('common.status')}
                    className='select-with-icon select-with-icon--border'
                    colorChoices={editableChoices}
                    defaultValue={defaultStatus}
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                    disabled={!canEditField('status')}
                    sx={{
                        '& .MuiFormHelperText-root': {
                            display: 'none',
                        },
                    }}
                />
            }
            canEdit={canEditField('status')}
            className={isEditing(fields.STATUS) ? 'editable-field--active' : ''}
        />
    );
};

const PriorityField = ({ translate, isEditing, fields, onEdit, canEditField }) => {
    const priorityChoices = serviceRequestPriorityChoices(translate);

    return (
        <EditableField
            isEditing={isEditing(fields.PRIORITY)}
            onEdit={canEditField('priority') ? onEdit : noop}
            displayValue={record => {
                const priority = priorityChoices.find(choice => choice.id === record.priority);
                return priority ? priority.name : record.priority;
            }}
            isFilled={record => {
                const priority = priorityChoices.find(choice => choice.id === record.priority);
                return priority ? priority.name : record.priority;
            }}
            colorChoice={record => {
                return priorityChoices.find(choice => choice.id === record.priority);
            }}
            editComponent={
                <SelectWithIcon
                    source='priority'
                    label={translate('common.priorityLabel')}
                    className='select-with-icon select-with-icon--outlined'
                    colorChoices={priorityChoices}
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                    disabled={!canEditField('priority')}
                />
            }
            canEdit={canEditField('priority')}
            className={isEditing(fields.PRIORITY) ? 'editable-field--active' : ''}
        />
    );
};

const TypeField = ({ resource, translate, isEditing, fields, onEdit, canEditField }) => {
    if (resource !== 'service-request') return null;

    const typeChoices = serviceRequestTypeChoices(translate);

    return (
        <EditableField
            isEditing={isEditing(fields.TYPE)}
            onEdit={canEditField('type') ? onEdit : noop}
            displayValue={record => {
                const type = typeChoices.find(typeChoice => typeChoice.id === record.type);
                return type ? type.name : record.type;
            }}
            isFilled={record => {
                const type = typeChoices.find(typeChoice => typeChoice.id === record.type);
                return type ? type.name : record.type;
            }}
            colorChoice={record => {
                return typeChoices.find(choice => choice.id === record.type);
            }}
            editComponent={
                <SelectWithIcon
                    source='type'
                    label={translate('resources.serviceRequest.labels.typeLabel')}
                    className='select-with-icon select-with-icon--outlined'
                    colorChoices={typeChoices}
                    defaultValue='mechanicalFailure'
                    validate={required(translate('common.validation.required'))}
                    fullWidth
                    disabled={!canEditField('type')}
                />
            }
            canEdit={canEditField('type')}
            className={isEditing(fields.TYPE) ? 'editable-field--active' : ''}
        />
    );
};

const ServiceNoteField = ({ translate, isEditing, fields, onEdit, canEditField }) => {
    return (
        <EditableField
            isEditing={isEditing(fields.SERVICE_NOTE)}
            onEdit={canEditField('serviceNote') ? onEdit : noop}
            displayValue={record => {
                const serviceNote = record?.collectionPoint?.serviceNote;
                return serviceNote || translate('resources.serviceRequest.labels.noNote');
            }}
            isFilled={record => !!record?.collectionPoint?.serviceNote}
            isHtml={true}
            editComponent={
                <CustomRichText
                    source='collectionPoint.serviceNote'
                    fullWidth
                    validate={maxLength(1000, translate('common.validation.maxLength', { count: 1000 }))}
                />
            }
            canEdit={canEditField('serviceNote')}
            className={isEditing(fields.SERVICE_NOTE) ? 'editable-field--active' : ''}
        />
    );
};

const CoordinatorField = ({ translate, isEditing, fields, onEdit, canEditField }) => {
    return (
        <EditableField
            isEditing={isEditing(fields.COORDINATOR)}
            onEdit={canEditField('coordinator') ? onEdit : noop}
            displayValue={record => {
                const coordinatorName = `${record.coordinator?.firstName} ${record.coordinator?.lastName}`;
                return record.coordinator ? coordinatorName : translate('resources.device.fields.notSpecified');
            }}
            isFilled={record => record.coordinator}
            editComponent={<CoordinatorInput label='' />}
            canEdit={canEditField('coordinator')}
            className={isEditing(fields.COORDINATOR) ? 'editable-field--active' : ''}
        />
    );
};

const ServicemanField = ({ translate, isEditing, fields, onEdit, canEditField }) => {
    return (
        <EditableField
            isEditing={isEditing(fields.SERVICEMAN)}
            onEdit={canEditField('serviceman') ? onEdit : noop}
            displayValue={record => {
                const servicemanName = `${record.serviceman?.firstName} ${record.serviceman?.lastName}`;
                return record.serviceman ? servicemanName : translate('resources.device.fields.notSpecified');
            }}
            isFilled={record => record.serviceman}
            editComponent={<ServicemanInput label='' />}
            canEdit={canEditField('serviceman')}
            className={isEditing(fields.SERVICEMAN) ? 'editable-field--active' : ''}
        />
    );
};

const UsedPartsField = ({ label, translate, isEditing, fields, onEdit, canEditField }) => (
    <EditableField
        label={label}
        isEditing={isEditing(fields.USED_PARTS)}
        onEdit={canEditField('usedParts') ? onEdit : noop}
        displayValue={record => {
            const parts = record.usedParts ?? [];
            if (!parts.length) return translate('resources.device.fields.notSpecified');
            return parts.map(part => part.name).join(', ');
        }}
        isFilled={record => Array.isArray(record.usedParts) && record.usedParts.length > 0}
        editComponent={<UsedPartsInput label='' />}
        canEdit={canEditField('usedParts')}
        className={isEditing(fields.USED_PARTS) ? 'editable-field--active' : ''}
    />
);

const WorkingTimeField = ({ translate, isEditing, fields, onEdit, canEditField }) => {
    return (
        <EditableField
            isEditing={isEditing(fields.WORKING_TIME)}
            onEdit={canEditField('workingTime') ? onEdit : noop}
            displayValue={record => record.workingTime?.toString() || translate('resources.device.fields.notSpecified')}
            isFilled={record => record.workingTime}
            editComponent={
                <NumberInput
                    label=''
                    source='workingTime'
                    fullWidth
                    min={0}
                    autoFocus={isEditing(fields.WORKING_TIME)}
                    validate={[required(translate('common.validation.required')), minValue(0)]}
                />
            }
            canEdit={canEditField('workingTime')}
            className={isEditing(fields.WORKING_TIME) ? 'editable-field--active' : ''}
        />
    );
};

const DifficultyField = ({ label, translate, isEditing, fields, onEdit, canEditField }) => {
    const difficultyChoices = [1, 2, 3, 4, 5].map(value => ({ id: value, name: value.toString() }));

    return (
        <EditableField
            label={label}
            isEditing={isEditing(fields.DIFFICULTY)}
            onEdit={canEditField('difficulty') ? onEdit : noop}
            displayValue={record => (record.difficulty ? record.difficulty.toString() : translate('resources.device.fields.notSpecified'))}
            isFilled={record => !!record.difficulty}
            editComponent={
                <SelectInput
                    source='difficulty'
                    label=''
                    choices={difficultyChoices}
                    emptyText={translate('resources.device.fields.notSpecified')}
                    parse={value => (value === '' ? null : value)}
                    fullWidth
                    disabled={!canEditField('difficulty')}
                />
            }
            canEdit={canEditField('difficulty')}
            className={isEditing(fields.DIFFICULTY) ? 'editable-field--active' : ''}
        />
    );
};

const PlannedDateField = ({ label, resource, isEditing, fields, onEdit, canEditField }) => {
    if (resource !== 'service-inspection') return null;

    return (
        <EditableField
            label={label}
            isEditing={isEditing(fields.PLANNED_DATE)}
            onEdit={canEditField('plannedDate') ? onEdit : noop}
            displayValue={record => {
                if (!record.scheduledDate) return '-';
                const date = new Date(record.scheduledDate);
                return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
            }}
            isFilled={record => !!record.scheduledDate}
            editComponent={
                <DateInput id='scheduledDate' className='date-input' source='scheduledDate' label='' fullWidth autoFocus={isEditing(fields.PLANNED_DATE)} />
            }
            canEdit={canEditField('plannedDate')}
            className={isEditing(fields.PLANNED_DATE) ? 'editable-field--active' : ''}
        />
    );
};

StatusField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
    defaultStatus: PropTypes.string.isRequired,
    canEditLimitedFields: PropTypes.bool.isRequired,
};

PriorityField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

TypeField.propTypes = {
    resource: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

ServiceNoteField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

CoordinatorField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

ServicemanField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

UsedPartsField.propTypes = {
    label: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

WorkingTimeField.propTypes = {
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

DifficultyField.propTypes = {
    label: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

PlannedDateField.propTypes = {
    label: PropTypes.string.isRequired,
    resource: PropTypes.string.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
};

PlannedDateField.propTypes = {
    label: PropTypes.string.isRequired,
    isEditing: PropTypes.func.isRequired,
    fields: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    canEditField: PropTypes.func.isRequired,
    className: PropTypes.string,
};

const ServiceSummary = ({ elementId, save, editingStates, defaultStatus, canEditAllFields, canEditLimitedFields, resource }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const {
        isEditing,
        FIELDS,
        startEditingStatus,
        startEditingPriority,
        startEditingType,
        startEditingCoordinator,
        startEditingServiceman,
        startEditingWorkingTime,
        startEditingServiceNote,
        startEditingPlannedDate,
        startEditingDifficulty,
        startEditingUsedParts,
    } = editingStates;

    const canEditField = buildCanEditField(canEditAllFields, canEditLimitedFields);

    const handleSave = async formData => {
        const transformedData = buildServiceSummaryPayload({ formData, record, isEditing, fields: FIELDS });
        return save(transformedData);
    };
    const isServiceInspection = resource === 'service-inspection';

    return (
        <Edit
            id={elementId}
            resource={resource}
            actions={false}
            sx={{
                '& .MuiCardContent-root': {
                    marginBottom: '0 !important',
                },
                '& form': {
                    paddingBottom: '0 !important',
                },
                '& .details-simple-show-layout': {
                    '& .MuiStack-root.ra-field.editable-field--active': {
                        padding: '32px 0',
                        '& .MuiTypography-root.RaLabeled-label': {
                            alignSelf: 'flex-start',
                            [`@media (min-width: ${breakpoints.values.md}px)`]: {
                                alignSelf: 'center',
                            },
                        },
                    },
                    '& .MuiTypography-root.RaLabeled-label': {
                        [`@media (min-width: ${breakpoints.values.md}px)`]: {
                            alignSelf: 'center',
                        },
                    },
                    '& .planned-date-field': {
                        '& > span.MuiTypography-body2': {
                            width: '100%',
                            '& .MuiFormHelperText-root': {
                                display: 'none',
                            },
                        },
                    },
                },
            }}
        >
            <SimpleForm toolbar={false} onSubmit={handleSave}>
                <SimpleShowLayout className='details-simple-show-layout'>
                    <FunctionField
                        label={translate('common.collectionPoint')}
                        sortBy='collectionPoint.customId'
                        render={data => <DeviceCollectionPointField collectionPoint={data.collectionPoint} />}
                    />
                    <FunctionField
                        className='serial-number-field'
                        label={translate('resources.serviceRequest.labels.serialNumberLabel')}
                        render={record => <DrawerLink resource='device' action='show' source={record.device.id} label={record.device.serialNumber} relative />}
                    />
                    {isServiceInspection && (
                        <FunctionField
                            label={translate('resources.serviceInspection.labels.type')}
                            render={record => {
                                return record.type === 'warranty'
                                    ? translate('resources.serviceInspection.labels.warranty')
                                    : translate('resources.serviceInspection.labels.postWarranty');
                            }}
                        />
                    )}
                    {isServiceInspection && (
                        <FunctionField
                            sx={{ '& .service-date-type, & .device-status-field-time': { display: 'none' } }}
                            label={translate('resources.serviceInspection.labels.dueDate')}
                            render={record => (record.dueDate ? <ServiceDate formatSince={record.dueDate} type={record.type} /> : '-')}
                        />
                    )}
                    {isServiceInspection && (
                        <Box
                            id={SERVICE_SUMMARY_PLANNED_DATE_ANCHOR_ID}
                            className='field-container'
                            label={translate('resources.serviceInspection.labels.plannedDate')}
                        >
                            <PlannedDateField
                                label={translate('resources.serviceInspection.labels.plannedDate')}
                                resource={resource}
                                isEditing={isEditing}
                                fields={FIELDS}
                                onEdit={startEditingPlannedDate}
                                canEditField={canEditField}
                            />
                        </Box>
                    )}
                    <StatusField
                        label={translate('common.status')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingStatus}
                        canEditField={canEditField}
                        defaultStatus={defaultStatus}
                        canEditLimitedFields={canEditLimitedFields}
                    />
                    <PriorityField
                        label={translate('common.priorityLabel')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingPriority}
                        canEditField={canEditField}
                    />
                    {resource !== 'service-inspection' && (
                        <TypeField
                            label={translate('resources.serviceRequest.labels.typeLabel')}
                            resource={resource}
                            translate={translate}
                            isEditing={isEditing}
                            fields={FIELDS}
                            onEdit={startEditingType}
                            canEditField={canEditField}
                        />
                    )}
                    <FunctionField
                        className='present-state'
                        label={translate('resources.serviceRequest.labels.deviceStateTitle')}
                        render={data => <DeviceStateField record={data.device} />}
                    />
                    <ServiceNoteField
                        label={translate('resources.serviceRequest.labels.serviceNoteCollectionPoint')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingServiceNote}
                        canEditField={canEditField}
                    />
                    <CoordinatorField
                        label={translate('resources.serviceRequest.labels.coordinator')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingCoordinator}
                        canEditField={canEditField}
                    />
                    <ServicemanField
                        label={translate('resources.serviceRequest.labels.serviceman')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingServiceman}
                        canEditField={canEditField}
                    />
                    <Box
                        id={SERVICE_SUMMARY_WORKING_TIME_ANCHOR_ID}
                        label={translate('resources.serviceRequest.labels.workingTimeLabel')}
                        className='field-container'
                    >
                        <WorkingTimeField
                            label={translate('resources.serviceRequest.labels.workingTimeLabel')}
                            translate={translate}
                            isEditing={isEditing}
                            fields={FIELDS}
                            onEdit={startEditingWorkingTime}
                            canEditField={canEditField}
                        />
                    </Box>
                    <UsedPartsField
                        label={translate('resources.serviceRequest.labels.usedParts')}
                        translate={translate}
                        isEditing={isEditing}
                        fields={FIELDS}
                        onEdit={startEditingUsedParts}
                        canEditField={canEditField}
                    />
                    {resource === 'service-request' && (
                        <DifficultyField
                            label={translate('resources.serviceRequest.labels.difficulty')}
                            translate={translate}
                            isEditing={isEditing}
                            fields={FIELDS}
                            onEdit={startEditingDifficulty}
                            canEditField={canEditField}
                        />
                    )}
                    <FunctionField
                        label={translate('resources.serviceRequest.labels.reportDate')}
                        render={record => formatTimePassedExpanded(record.createdAt, translate)}
                    />
                    <FunctionField
                        label={translate('resources.product.fields.lastUpdate')}
                        render={record => formatTimePassedExpanded(record.updatedAt, translate)}
                    />
                    <FunctionField
                        label={translate('resources.serviceRequest.labels.sourceTitle')}
                        render={record => <ServiceRequestSourceField record={record} />}
                    />
                </SimpleShowLayout>
            </SimpleForm>
        </Edit>
    );
};

ServiceSummary.propTypes = {
    elementId: PropTypes.string.isRequired,
    save: PropTypes.func.isRequired,
    editingStates: PropTypes.object.isRequired,
    defaultStatus: PropTypes.string.isRequired,
    canEditAllFields: PropTypes.bool.isRequired,
    canEditLimitedFields: PropTypes.bool.isRequired,
    resource: PropTypes.string.isRequired,
};

export default ServiceSummary;
